/* eslint-disable no-underscore-dangle */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, DestroyRef, EventEmitter, forwardRef, inject, Input,
  input, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminService } from '../../../admin/services/admin.service';
import { ConfirmDialogComponent } from '../../../main/confirm-dialog/confirm-dialog.component';
import { Category } from '../../../models/category.model';
import { Product } from '../../../models/product.model';
import { RoleService } from '../../../role/role.service';
import { CategoriesService } from '../../../services/categories.service';
import { InventoryActionService } from '../../../services/inventory-action.service';
import { debounceTime, pairwise, startWith } from 'rxjs';

import { ProductsDialogComponent } from '../products-dialog/products-dialog.component';
import { InventoryAction } from '../../../models/inventory_action.model';
import { ProductsService } from '../../../services/products.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MoneyPipe } from '../../pipes/money.pipe';
import { MatChipsModule } from '@angular/material/chips';
import { HumanPipe } from '../../pipes/human.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { ImageUploaderComponent } from '../../image-uploader/image-uploader.component';
import { at } from 'lodash-es';
import { MatBadge } from '@angular/material/badge';

interface Action {
  fn: string
  control: FormControl | FormGroup
  value: string | number
}

@Component(
  {
    selector: 'app-inventory-action-form',
    templateUrl: './inventory-action-form.component.html',
    styleUrls: ['./inventory-action-form.component.scss'],
    imports: [
      CdkTextareaAutosize,
      HumanPipe,
      NgClass,
      NgFor,
      NgIf,
      MatBadge,
      MatButtonModule,
      MatChipsModule,
      MatDatepickerModule,
      MatExpansionPanel,
      MatExpansionPanelHeader,
      MatFormFieldModule,
      MatIconModule,
      MatInputModule,
      MatTooltipModule,
      MoneyPipe,
      ReactiveFormsModule,
      TitleCasePipe,
      ImageUploaderComponent,
    ],
  },
)
export class InventoryActionFormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() brandId: number;

  @Input() canAddItem: boolean = true;
  
  @Input() canUpdate: boolean = false;

  checkForDiscounts = input<boolean>(false);
  
  @Input() clientBookingMode = false;

  compareTo = input<InventoryAction>();

  confirmDeletion = input<boolean>(true);
  
  @Input() enableAmount = true;
  
  @Input() enableDiscounts = true;
  
  @Input() enableEstimatedCompletionDate = false;
  
  @Input() filterCategories: number[];

  filterWarehouses = input<number[]>();

  focused = input<boolean>(true);
  
  @Input() gallery: any[] = [];

  onlyInventoriable = input<boolean>(false);
  
  @Input() inventoryLayout = false;
  
  @Input() isReadOnly = false;
  
  @Input() parentFormGroup: FormGroup;
  
  @Input() parentModel: string = 'check_in';
  
  @Input() photos: any[] = [];

  requirementId = input<number | null>(null);
  
  @Input() searchBy = 'product';

  sellMode = input<boolean>(true);

  showBuyPrice = input(false);
  
  @Input() showCategories = true;
  
  @Input() showTotals = true;
  
  @Input() simpleView = false;

  stockFilter = input<Record<string, any>>();

  validateStock = input<boolean>(false);


  readonly panelOpenState = signal(false);

  public canCreate: boolean;

  public canDestroy: boolean;

  public categories: Category[];

  actionHistory = signal<Action[]>([]);

  public mainCategory: Category;

  hasMainCategory: boolean;

  public form: FormGroup;

  subs = [];

  @Output() approved: EventEmitter<void> = new EventEmitter();

  @Output() dataChanged: EventEmitter<number> = new EventEmitter();

  @Output() declined: EventEmitter<void> = new EventEmitter();

  @ViewChild('expansionPanel') expansionPanel!: MatExpansionPanel;

  private readonly _cdr = inject(ChangeDetectorRef);

  protected readonly destroyRef = inject(DestroyRef);

  private readonly productService = inject(ProductsService);

  constructor(
    public categoriesService: CategoriesService,
    private fb: FormBuilder,
    private inventoryActionsService: InventoryActionService,
    private matDialog: MatDialog,
    private rolesService: RoleService,
    private snackBar: MatSnackBar,
    public adminService: AdminService,
  ) {
    this.rolesService.can('update', this.parentModel).subscribe((roleCanUpdate) => {
      this.canUpdate = this.canUpdate || roleCanUpdate;
    });
    this.rolesService.can('create', this.parentModel).subscribe((canCreate) => {
      this.canCreate = canCreate;
    });
    this.rolesService.can('destroy', this.parentModel).subscribe((canDestroy) => {
      this.canDestroy = canDestroy;
    });
  }

  get parentFormArray() {
    return this.parentFormGroup.get('inventory_action_products') as FormArray;
  }

  get commentControl() : FormControl{
    return this.parentFormGroup.get('comment') as FormControl;
  }

  get estimatedCompletionDateControl() : FormControl {
    return this.parentFormGroup.get('estimated_completion_date') as FormControl;
  }

  get expirationDateControl() {
    return this.parentFormGroup.get('expiration_date') as FormControl;
  }

  get parentDocumentOnApproval() {
    return this.parentDocument?.status?.approval;
  }

  get status() {
    return this.parentFormGroup.getRawValue()?.status;
  }

  get parentDocument() {
    return this.parentFormGroup.getRawValue()?.document;
  }

  get isApproved() {
    return this.status?.completed || this.status?.approved;
  }

  get isDeclined() {
    return this.status?.failed;
  }

  get isLocked() {
    return this.status?.locked;
  }

  get isParentLocked() {
    return this.parentDocument?.status?.locked;
  }

  get completed() {
    return (this.isApproved || this.isDeclined) && !this.parentFormGroup.getRawValue()?.draft;
  }

  ngOnInit() {
    this.categoriesService.categories.subscribe((categories) => {
      this._cdr.markForCheck();
      this.categories = categories;
      
      this.mainCategory = categories.find((category) => category.use_as_name);

      // Filter categories
      if (this.filterCategories?.length) {
        this.categories = this.categories.filter((category) => this.filterCategories.includes(category.id));
      }

      this.hasMainCategory = this.categories.some((category) => category.id === this.mainCategory?.id);
      this.initializeForm();
    });
  }

  ngAfterViewInit(): void {
    const onPaste = () => {
      if (!this.focused()) return;

      navigator.clipboard.readText().then((text) => {
        if (!text || this.isReadOnly || !this.canUpdate || !this.canAddItem || this.isLocked || this.isParentLocked) return;

        const skus = text.split('\n').filter((sku) => sku.length > 0);
        const plural = skus.length > 1;
        this._cdr.markForCheck();
        this.snackBar.open(`Pegando ${skus.length} producto${plural ? 's':''}`, 'Cerrar', {
          duration: 3000,
          verticalPosition: 'bottom',
          horizontalPosition: 'center',
        });
        Promise.allSettled(skus.map((sku) => this.pasteProduct(sku.trim()))).then((results) => {
          this._cdr.markForCheck();

          const failed = results.filter((result) => result.status === 'rejected');
          const succeded = results.filter((result) => result.status === 'fulfilled');
          const succededPlural = succeded.length > 1;
          let duration = 3000;
          let message = '';
          if (failed.length) {
            duration = 6000;
            message += failed.map((result) => result.reason).join('. ') + '. ';
          }

          if (succeded.length) {
            message += `${succeded.length} producto${succededPlural ? 's':''} pegado${succededPlural ? 's':''}.`;
          }
          this.snackBar.open(message, 'Cerrar', {
            duration,
            verticalPosition: 'bottom',
            horizontalPosition: 'center',
            panelClass: ['snackbar-success']
          });
        })
      });
    }

    const onKeyPress = (e) => {
      if (e.ctrlKey && e.keyCode == 90) {
        e.preventDefault();
        if (!this.focused()) return;
        
        this.actionHistory.update((history) => {
          const lastAction = history.pop();
          if (!lastAction) return history;

          this[lastAction.fn](lastAction.control, lastAction.value);

          this._cdr.markForCheck();
          return [...history];
        });
      }
    }

    document.addEventListener('keydown', onKeyPress);
    // document.addEventListener('paste', onPaste);

    // this.subs.push({ event: 'keydown', handler: onKeyPress }, { event: 'paste', handler: onPaste });
    this.subs.push({ event: 'keydown', handler: onKeyPress });
  }

  onImagesUploaded(pictures) {
    const picturesArray = this.parentFormGroup.get('pictures') as FormArray;
    picturesArray.clear();
    pictures.forEach((picture) => {
      picturesArray.push(this.fb.group(picture));
    });
    this.emitTotal();
  }

  togglePanel() {
    if (this.expansionPanel.expanded) {
      this.expansionPanel.close();
    } else {
      this.expansionPanel.open();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.parentFormGroup && !changes.parentFormGroup.firstChange && this.categories) {
      this.initializeForm();
    }
  }

  initializeForm() {
    // Remove products from categories that are not included in the filter
    const parentFormArrayControls = [...this.parentFormArray?.controls];
    parentFormArrayControls?.forEach((control) => {
      const categoryId = control.get('category_id').value;
      if (!this.categories.some((category) => category.id === categoryId)) {
        this.parentFormArray.removeAt(this.parentFormArray.controls.indexOf(control));
      }
    });

    this.form = this.fb.group({});
    this.categories.forEach((category) => {
      this.form.addControl(category.id.toString(), this.fb.array([]));
    });

    if (!this.canUpdate || this.isLocked || this.isParentLocked || this.isReadOnly) {
      this.parentFormGroup.get('comment')?.disable();
      this.parentFormGroup.get('estimated_completion_date')?.disable();
    } else {
      this.parentFormGroup.get('comment')?.valueChanges?.pipe(debounceTime(1000))?.subscribe(() => {
        this.emitTotal();
      });
      this.parentFormGroup.get('estimated_completion_date')?.valueChanges?.pipe(debounceTime(1000))?.subscribe(() => {
        this.emitTotal();
      });
      this.parentFormGroup.get('expiration_date')?.valueChanges?.pipe(debounceTime(1000))?.subscribe(() => {
        this.emitTotal();
      });
    }

    this.prefillForm();
  }

  private prefillForm() {
    const parentFormArrayControls = [...this.parentFormArray?.controls];
    parentFormArrayControls?.forEach((control: FormGroup, index) => {
      const product = control.getRawValue();
      let categoryFormArray = this.getCategoryFormArray(product.category_id);

      if (!categoryFormArray) {
        categoryFormArray = this.fb.array([]);
        this.form.setControl(product.category_id.toString(), categoryFormArray);
      }

      categoryFormArray.push(control);
      this.addFormHandlersAndValidators(control as FormGroup);
    });
  }

  onAddItem(category_id: number) {
    this.selectProduct(category_id);
  }

  onReplaceItem(category_id: number, index: number) {
    this.replaceProduct(category_id, index);
  }

  onRemoveItem(category_id: number, index: number) {
    if (!this.confirmDeletion()) {
      this.removeRow(category_id, index);
      return;
    }

    this.matDialog.open(
      ConfirmDialogComponent,
      { data: { title: 'Eliminar producto', body: '' } },
    )
      .afterClosed().subscribe((result) => {
        if (result) {
          this.removeRow(category_id, index);
        }
      });
  }

  removeRow(category_id: number, index: number) {
    const categoryArrayControl = this.form.get(category_id.toString()) as FormArray;
    categoryArrayControl.at(index)?.get('_destroy')?.setValue(true);

    if (!categoryArrayControl.at(index).get('id')?.value) {
      const ctlIdx = this.parentFormArray.controls.findIndex((control) => control === categoryArrayControl.at(index));
      this.parentFormArray.removeAt(ctlIdx);
    }

    this.actionHistory.update((history) => [
      ...history,
      { fn: 'add', control: categoryArrayControl.at(index) as FormGroup, value: null }
    ]);

    categoryArrayControl.removeAt(index);
    this._cdr.markForCheck();
  }

  selectProduct(categoryId: number) {
    const categoryName = this.categories.find((category) => category.id === categoryId)?.name;
    if (!categoryName) return;

    this.matDialog.open(ProductsDialogComponent, {
      data: { 
        brandId: this.brandId,
        categoryId, 
        categoryName, 
        checkForDiscounts: this.checkForDiscounts(),
        onlyInventoriable: this.onlyInventoriable(),
        requirementId: this.requirementId(),
        showBuyPrice: this.showBuyPrice(),
        warehouseIds: this.filterWarehouses(),
        ...this.stockFilter(),
      },
      minWidth: '80%',
    }).afterClosed().subscribe((product: Partial<Product>) => {
      if (!product) return;

      if (typeof product === 'object') {
        this.addProductControl(product, categoryId);
        
      } else if (typeof product === 'string') {
        this.parentFormGroup.get('comment').setValue(product);
      }
    });
  }

  replaceProduct(categoryId: number, index: number) {
    const categoryFormArray = this.getCategoryFormArray(categoryId);
    const currentProduct = categoryFormArray.at(index).get('product_id').value;
    const categoryName = this.categories.find((category) => category.id === categoryId)?.name;
    this.matDialog.open(ProductsDialogComponent, {
      data: {
        brandId: this.brandId,
        categoryId, 
        categoryName,
        checkForDiscounts: this.checkForDiscounts(),
        onlyInventoriable: this.onlyInventoriable(),
        requirementId: this.requirementId(),
        showBuyPrice: this.showBuyPrice(),
        warehouseIds: this.filterWarehouses(),
        currentProduct,
        ...this.stockFilter(),
      },
      minWidth: '80%',
    }).afterClosed().subscribe((product: Partial<Product> | string) => {
      if (!product) return;

      if (typeof product === 'object') {
        const group = categoryFormArray.at(index)
        this.actionHistory.update((history) => [
          ...history,
          { fn: 'update', control: group as FormGroup, value: group.getRawValue() }
        ]);
        this.updateRow(group as FormGroup, product, categoryId);
      } else if (typeof product === 'string') {
        this.parentFormGroup.get('comment').setValue(product);
      }
    });
  }

  pasteProduct(sku: string) {
    return new Promise<void>((resolve, reject) => {
      const alreadyAdded = this.parentFormArray.controls.find(
        (ctl) => ctl.get('product.code')?.value === sku,
      );
  
      if (alreadyAdded) {
        alreadyAdded.get('quantity').setValue(alreadyAdded.get('quantity').value + 1);
        resolve();
        return;
      }
  
      this.productService.select(sku, null, null, this.filterWarehouses()).subscribe((products) => {
        const product = products.items.at(0);
        if (!product) {
          reject(`No se encontró el producto con SKU: ${sku}`);
          return;
        }
  
        const categoryId = product.category_ids.at(0);
        if (!categoryId) return;
        if (!this.categories.map(c => c.id).includes(categoryId)) {
          reject(`El producto ${sku} pertenece a una categoría inválida`);
          return;
        }
  
        this.addProductControl(product, categoryId);
        resolve();
      });
    });

  }

  addProductControl(product: Partial<Product>, categoryId: number) {
    const categoryFormArray = this.form.get(categoryId.toString()) as FormArray;
    const alreadyAdded = categoryFormArray.controls.find(
      (ctl) => ctl.get('product_id')?.value === product.id,
    );

    if (alreadyAdded) {
      this._cdr.markForCheck();
      if (alreadyAdded.get('_destroy')?.value) {
        alreadyAdded.get('_destroy')?.setValue(false);
      } else {
        alreadyAdded.get('quantity').setValue(alreadyAdded.get('quantity').value + 1);
      }
    } else {
      const control = this.generateFormGroup();
      categoryFormArray.push(control);
      this._cdr.detectChanges();
      this.addProductRow(product, categoryId, categoryFormArray.length - 1);
      this.actionHistory.update((history) => [...history, { fn: 'remove', control, value: null }]);
    }
  }

  addProductRow(product: Partial<Product>, categoryId: number, index?: number) {
    const categoryFormArray = this.getCategoryFormArray(categoryId);

    const alreadyAdded = categoryFormArray.controls.find(
      (ctl) => ctl.get('product_id')?.value === product.id,
    );

    if (alreadyAdded) {
      alreadyAdded.get('quantity').markAsTouched();
      alreadyAdded.get('_destroy')?.setValue(false);
      return;
    }
    const control = categoryFormArray.at(index || categoryFormArray.length - 1) as FormGroup;
    this.updateRow(control, product, categoryId);
    this.parentFormArray.push(control);
  }

  private updateRow(rowGroup: FormGroup, product: Partial<Product>, categoryId: number) {
    rowGroup.get('product_id')?.setValue(product.id);
    rowGroup.get('category_id')?.setValue(categoryId);
    rowGroup.get('product.code')?.setValue(product.code);
    rowGroup.get('unit')?.setValue(product.unit);
    rowGroup.get('discount_percent')?.enable();
    rowGroup.get('discount_percent')?.patchValue(product.discount_percent || 0, { emitEvent: false });

    if (this.sellMode()) {
      rowGroup.get('amount')?.setValue(product.price, { emitEvent: false });
    } else {
      rowGroup.get('amount')?.setValue(product.buy_price, { emitEvent: false });
    }

    rowGroup.get('product.name')?.setValue(product.name);
    if (this.isLocked || this.isParentLocked  || !this.canUpdate || this.isReadOnly) {
      rowGroup.get('quantity')?.disable();
    } else {
      rowGroup.get('quantity')?.enable();
    }
    rowGroup.get('quantity')?.setValue(1, { emitEvent: false });
    rowGroup.get('_destroy')?.setValue(false);
    rowGroup.get('_edited')?.setValue(true);

    if (this.validateStock()) {
      rowGroup.get('quantity').setValidators([Validators.required, Validators.min(1), Validators.max(product.stock_on_hand)]);
      rowGroup.addControl('stock_on_hand', this.fb.control(product.stock_on_hand));
    }
    this._cdr.markForCheck();
  }

  getCategoryFormArray(categoryId: number) : FormArray{
    return (this.form?.get(categoryId.toString()) as FormArray);
  }
  getCategoryFormControl(categoryId: number, index:number, controlName:string) : FormControl{
    return (this.form?.get(categoryId.toString()) as FormArray).at(index).get(controlName) as FormControl;
  }

  getCategoryTotal(categoryId?: number) {
    if (!this.form) {
      return 0;
    }
    const formValue = this.form.getRawValue();

    const total = Object.keys(formValue)
      .filter((key) => key.toString() === categoryId.toString())
      .map((key) => formValue[key])
      .reduce((acc, val) => acc.concat(val), [])
      .filter((control) => !control._destroy)
      .map((control) => (control.amount || 0) * (control.quantity || 0))
      .reduce((acc, val) => acc + val, 0);

    return total;
  }

  getTotal() {
    if (!this.form) {
      return 0;
    }
    const formValue = this.form.getRawValue();

    const total = Object.keys(formValue)
      .map((key) => formValue[key])
      .reduce((acc, val) => acc.concat(val), [])
      .filter((control) => !control._destroy)
      .map((control) => (control.amount || 0) * (control.quantity || 0) * (1 - (control.discount_percent || 0) / 100))
      .reduce((acc, val) => acc + val, 0);

    return total;
  }

  reset() {
    if (!this.compareTo()) this.clear();

    this.parentFormArray.clear();
    Object.keys(this.form.controls).forEach((controlKey) => {
      (this.form.get(controlKey) as FormArray).clear();
    });

    this.compareTo().inventory_action_products.forEach((product) => {
      const control = this.generateFormGroup(product);
      control.get('quantity').setValue(0, { emitEvent: false });
      this.parentFormArray.push(control);
      this.getCategoryFormArray(product.category_id).push(control);
    });

  }

  private generateFormGroup({
    id = null,
    category_id = null,
    product: {
      name = '',
      code = '',
    } = {},
    discount_percent = 0,
    flexible = false,
    kit_name = '',
    kit_product_quantity = null,
    amount = 0,
    product_id = null,
    quantity = 1,
    unit = '',
    _destroy = false,
    _edited = false,
  } = {}) : FormGroup {
    const controlGroup = this.fb.group({
      id,
      product_id,
      product: this.fb.group({
        name,
        code,
      }),
      discount_percent: [{ value: discount_percent, disabled: !discount_percent }],
      flexible,
      quantity: [{ value: quantity, disabled: !quantity }],
      unit,
      amount,
      category_id,
      kit_name,
      kit_product_quantity,
      _destroy,
      _edited,
    });

    this.addFormHandlersAndValidators(controlGroup);

    return controlGroup;
  }

  addFormHandlersAndValidators(controlGroup: FormGroup) {
    controlGroup.get('product_id').setValidators(Validators.required);
    controlGroup.get('category_id').setValidators(Validators.required);
    controlGroup.get('product.code').setValidators(Validators.required);
    controlGroup.get('quantity').setValidators([Validators.required, Validators.min(1)]);
    controlGroup.get('unit').disable();
    controlGroup.get('unit').setValidators(Validators.required);
    controlGroup.get('amount').setValidators([Validators.required, Validators.min(0)]);
    controlGroup.get('product.name').disable();
    controlGroup.get('kit_name')?.disable();
    controlGroup.get('kit_product_quantity')?.disable();
    if (this.enableDiscounts) {
      controlGroup.get('discount_percent').setValidators([Validators.min(0), Validators.max(100)]);
    }

    const category = this.categories.find((cat) => cat.id === controlGroup.get('category_id').value);
    if (category?.name === 'Adicionales') {
      controlGroup.get('amount').disable();
      controlGroup.get('_edited')?.setValue(true);
    }

    if (!this.canUpdate || this.isReadOnly) {
      controlGroup.disable();
    }

    if (this.isLocked || this.isParentLocked || !this.canUpdate || this.isReadOnly) {
      controlGroup.get('quantity').disable();
      controlGroup.get('product.code').disable();
      controlGroup.get('amount').disable();
    } else {
      this.registerChangesOn(controlGroup, 'amount');
      this.registerChangesOn(controlGroup, 'quantity');
      this.registerChangesOn(controlGroup, 'discount_percent');

      controlGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(1000)).subscribe((data) => {
        if (!data._destroy && (!data.quantity)) return;
        this.emitTotal();
      });
    }
  }

  private registerChangesOn(controlGroup: FormGroup, fieldName: string) {
    if (!controlGroup.get(fieldName)) return;

    const currentValue = controlGroup.get(fieldName).value;

    controlGroup.get(fieldName)
      .valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(currentValue), pairwise())
      .subscribe(([prev, curr]: [number, number]) =>  {
        if (prev === curr) return;
        this.actionHistory.update((history) => [
          ...history,
          { fn: 'setValue', control: controlGroup.get(fieldName) as FormControl, value: prev }
        ]);
      });
  }

  clear() {
    Object.keys(this.form.controls).forEach((controlKey) => {
      (this.form.get(controlKey) as FormArray).clear();
    });

    const persistedControls = [];
    this.parentFormArray.controls.forEach((control) => {
      if (control.get('id')?.value) {
        control.get('_destroy').setValue(true);
        persistedControls.push(control);
      }
    });

    this.parentFormArray.clear();
    persistedControls.forEach((control) => {
      this.parentFormArray.push(control);
    });
  }

  approveInventoryAction() {
    this.matDialog.open(
      ConfirmDialogComponent,
      { data: { title: '¿Aprobar item?', body: 'Esta acción es irreversible.' } },
    )
      .afterClosed().subscribe((result) => {
        if (result) {
          this.inventoryActionsService.approve(this.parentFormGroup.getRawValue().id).subscribe(() => {
            const statusData = this.parentFormGroup.get('status').value;
            this.parentFormGroup.get('status').setValue(
              {
                ...statusData, completed: true, initial: false, locked: true,
              },
            );
            this.approved.emit();
          });
        }
      });
  }

  declineInventoryAction() {
    this.matDialog.open(
      ConfirmDialogComponent,
      { data: { title: '¿Rechazar item?', body: 'Esta acción es irreversible.' } },
    )
      .afterClosed().subscribe((result) => {
        if (result) {
          this.inventoryActionsService.decline(this.parentFormGroup.getRawValue().id).subscribe(() => {
            const statusData = this.parentFormGroup.get('status').value;
            this.parentFormGroup.get('status').setValue(
              {
                ...statusData, failed: true, initial: false, locked: true,
              },
            );
            this.declined.emit();
          });
        }
      });
  }

  updateQuantity(categoryId: number, index: number, step: number) {
    const formArray = this.getCategoryFormArray(categoryId);
    const productControl = formArray.at(index);
    const currentQuantity = productControl.get('quantity')?.value || 0;
    const newQuantity = currentQuantity + step;

    if (newQuantity >= 0) {
      productControl.get('quantity')?.setValue(newQuantity);
    }
  }

  onQuantityInputBlur(categoryId: number, index: number): void {
    const formArray = this.getCategoryFormArray(categoryId);
    const productControl = formArray.at(index);
    let currentQuantity = productControl.get('quantity')?.value;

    currentQuantity = Number(currentQuantity);

    if (Number.isNaN(currentQuantity) || currentQuantity <= 0) {
      if (!this.confirmDeletion()) {
        this.removeRow(categoryId, index);
        return;
      }

      this.matDialog.open(
        ConfirmDialogComponent,
        { data: { title: 'Eliminar producto', body: '' } },
      ).afterClosed().subscribe((result) => {
        if (result) {
          this.removeRow(categoryId, index);
        } else {
          productControl.get('quantity')?.setValue(1);
        }
      });
    } else {
      productControl.get('quantity')?.setValue(currentQuantity);
    }
  }

  blockNegativeInput(event: KeyboardEvent) {
    if (event.key === '-' || event.key === '+') {
      event.preventDefault();
    }
  }

  handleInput(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  emitTotal() {
    this.dataChanged.emit(this.getTotal());
  }

  get discountTotal() {
    return this.parentFormGroup.getRawValue().inventory_action_products?.map(
      (product) => product.amount * ((product.discount_percent || 0) / 100) * product.quantity,
    ).reduce((a, b) => a + b, 0) || 0;
  }

  hasProducts(): boolean {
    if (!this.categories || this.categories.length === 0) {
      return false;
    }
    return this.categories.some((category) => this.getCategoryFormArray(category.id).controls.length > 0);
  }

  getDifferenceMessage(product, attr: string): string {
    if (!this.compareTo()) return null;

    const found = this.compareTo().inventory_action_products.find(
      (originalProduct) => originalProduct.product_id === product.product_id,
    );

    const type = this.adminService.getText(this.compareTo().type);

    if (!found) return;

    if (found[attr] !== product[attr]) {
      return `${this.adminService.getText(attr)}: Hay discrepancia con respecto a ${type} (${found[attr]})`
    }

    return;
  }

  getNotFoundMessage(product): string {
    if (!this.compareTo()) return null;

    const found = this.compareTo().inventory_action_products.find(
      (originalProduct) => originalProduct.product_id === product.product_id,
    );

    if (!found) {
      const type = this.adminService.getText(this.compareTo().type);
      return `Este producto no está incluído en ${type}`;
    }

    return null;
  }

  getUnsufficientStockMessage(product): string {
    if (!this.validateStock()) return null;

    const stockOnHand = product.stock_on_hand;
    const quantity = product.quantity;

    if (stockOnHand < quantity) {
      return `Stock insuficiente: ${stockOnHand} en stock`;
    }

    return null;
  }

  getAllWarningMessages(product) {
    return [
      this.getNotFoundMessage(product),
      this.getDifferenceMessage(product, 'quantity'),
      this.getDifferenceMessage(product, 'amount'),
      this.getUnsufficientStockMessage(product),
    ].filter((message) => message).join('\n');
  }

  get missingProducts() {
    return this.compareTo()?.inventory_action_products.filter(
      (product) => !this.parentFormArray.controls.find(
        (control) => control.get('product_id').value === product.product_id,
      ),
    ) || [];
  }

  private remove(control: FormGroup) {
    if (control.get('id').value) {
      control.get('_destroy').setValue(true);
    } else {
      const ctlIdx = this.parentFormArray.controls.findIndex((ctl) => ctl === control);
      this.parentFormArray.removeAt(ctlIdx);
      const categoryFormArray = this.getCategoryFormArray(control.get('category_id').value);
      const ctlCatIdx = categoryFormArray.controls.findIndex((ctl) => ctl === control);
      categoryFormArray.removeAt(ctlCatIdx);
    }
  }

  private add(control: FormGroup) {
    const categoryFormArray = this.getCategoryFormArray(control.get('category_id').value);
    categoryFormArray.push(control);

    if (!control.get('id')?.value) {
      this.parentFormArray.push(control);
    }
    
    control.get('_destroy').setValue(false);
  }

  private setValue(control: FormControl, value: number) {
    control.setValue(value);
  }

  private update(control: FormGroup, product: Partial<Product>) {
    control.patchValue(product, { emitEvent: false, onlySelf: true });
    this.emitTotal();
  }


  ngOnDestroy(): void {
    this.subs.forEach((sub) => document.removeEventListener(sub.event, sub.handler));
  }
}
